// Printer idarəetmə ekranı — YALNIZ POS rejimi (pos_local) üçün.
//
// Cloud-da printer konsepti yoxdur; bu səhifə birbaşa `printerGateway` (posRequest)
// vasitəsilə pos_local /v1/printers endpoint-lərinə gedir. Yazma əməliyyatları
// backend-də manager+ rol tələb edir (rol yoxdursa 403 → toast göstərilir).
//
// Əhatə (Mərhələ): siyahı + əlavə/redaktə/sil + test çap.

import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import {
  Printer,
  Lock,
  Trash2,
  Eye,
  Zap,
  X,
  Wifi,
  Usb,
  FileText,
  RefreshCw,
  PowerOff,
  CheckCircle2,
} from "lucide-react";
import { toast } from '../components/ui/notify';

import { useTheme } from "../context/ThemeContext";
import { useI18n } from "../i18n/I18nContext";
import { PosLoader } from "../components/pos/PosLoader";
import { useConfirm } from "../components/ConfirmDialog";
import {
  POS_ENABLED,
  listPrinters,
  deletePrinter,
  testPrinter,
  listPrintJobs,
  getPrintJobPreview,
  PosPrinter,
  PrinterStation,
  PrinterConnection,
  PosPrintJob,
  PosPrintJobPreview,
} from "../api/pos";

const STATIONS: { value: PrinterStation; labelKey: string }[] = [
  { value: "kitchen", labelKey: "printers.station.kitchen" },
  { value: "bar", labelKey: "printers.station.bar" },
  { value: "cashier", labelKey: "printers.station.cashier" },
  { value: "bill", labelKey: "printers.station.bill" },
  { value: "other", labelKey: "printers.station.other" },
];

const CONNECTIONS: {
  value: PrinterConnection;
  labelKey: string;
  icon: any;
  hint: string;
}[] = [
  { value: "tcp", labelKey: "printers.conn.tcp", icon: Wifi, hint: "192.168.1.100:9100" },
  { value: "usb", labelKey: "printers.conn.usb", icon: Usb, hint: "USB001 / device path" },
  { value: "file", labelKey: "printers.conn.file", icon: FileText, hint: "/path/to/spool" },
];

const PosPrinters: React.FC = () => {
  const { isDark } = useTheme();
  const { t } = useI18n();

  const stationLabel = (s: string) => {
    const k = STATIONS.find((x) => x.value === s)?.labelKey;
    return k ? t(k) : s;
  };

  const [loading, setLoading] = useState(true);
  const [printers, setPrinters] = useState<PosPrinter[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null); // test/toggle/delete üçün

  const { confirm, confirmDialog } = useConfirm();

  const [jobs, setJobs] = useState<PosPrintJob[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [preview, setPreview] = useState<PosPrintJobPreview | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listPrinters();
      if (res.ok && res.data) setPrinters(res.data);
      else toast.error(res.error?.message || t("printers.toast.loadFailed"));
    } catch (e) {
      toast.error(t("printers.toast.connectFailed"));
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Son çap işləri.
   *
   * NİYƏ: fiziki çek aparatı olmayan (yaxud offline) quraşdırmalarda "fiş
   * çıxdımı, üstündə nə yazılıb?" sualına cavab vermək mümkün deyildi — job
   * `pending`/`failed` qalırdı və məzmunu yalnız binar BLOB idi.
   */
  const loadJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const res = await listPrintJobs(15);
      if (res.ok && res.data) setJobs(res.data);
    } catch {
      /* çap tarixçəsi ikinci dərəcəlidir — səhifəni bloklamırıq */
    } finally {
      setJobsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (POS_ENABLED) {
      load();
      loadJobs();
    } else {
      setLoading(false);
    }
  }, [load, loadJobs]);

  /** Fişin mətn önizləməsini aç (ESC/POS baytları oxunaqlı mətnə çevrilir). */
  const openPreview = async (job: PosPrintJob) => {
    setPreviewOpen(true);
    setPreview(null);
    try {
      const res = await getPrintJobPreview(job.id);
      if (res.ok && res.data) setPreview(res.data);
      else toast.error(res.error?.message || t("printers.toast.previewFailed"));
    } catch {
      toast.error(t("common.networkError"));
    }
  };

  /**
   * Yalnız YERLİ (cloud-dan gəlməyən) printeri silir. Belə sətirlər adətən
   * admin panelindən əvvəl əl ilə yaradılmış qalıqlardır.
   */
  const handleDeleteLocal = async (p: PosPrinter) => {
    if (!(await confirm({
      message: t("printers.deleteConfirm", { name: p.name }),
      variant: "danger",
    }))) return;

    setBusyId(p.id);
    try {
      const res = await deletePrinter(p.id);
      if (res.ok) {
        toast.success(t("printers.toast.deleted"));
        await load();
      } else {
        toast.error(res.error?.message || t("printers.toast.opFailed"));
      }
    } catch {
      toast.error(t("common.networkError"));
    } finally {
      setBusyId(null);
    }
  };

  const handleTest = async (p: PosPrinter) => {
    setBusyId(p.id);
    try {
      const res = await testPrinter(p.id);
      if (res.ok) toast.success(t("printers.toast.testSent", { name: p.name }));
      else toast.error(res.error?.message || t("printers.toast.testFailed"));
    } catch {
      toast.error(t("printers.toast.noResponse"));
    } finally {
      setBusyId(null);
    }
  };

  const bg = isDark ? "bg-[#13161f]" : "bg-gray-50";
  const card = isDark ? "bg-[#1a1d27] border-[#2a2e3a]" : "bg-white border-gray-100";
  const text = isDark ? "text-gray-100" : "text-gray-900";
  const subText = isDark ? "text-gray-400" : "text-gray-500";

  // POS rejimi deyilsə — səhifə işləmir.
  if (!POS_ENABLED) {
    return (
      <div className={`min-h-[calc(100vh-80px)] flex items-center justify-center ${bg}`}>
        <div className={`text-center p-8 rounded-2xl border ${card}`}>
          <Printer className={`w-12 h-12 mx-auto mb-3 ${subText}`} />
          <h2 className={`text-lg font-semibold ${text}`}>{t("printers.disabledTitle")}</h2>
          <p className={`mt-1 text-sm ${subText}`}>
            {t("printers.disabledText")}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-[calc(100vh-80px)] ${bg} px-4 lg:px-8 py-6`}>
      <Helmet>
        <title>{t("printers.title")} · Rezmo POS</title>
      </Helmet>
      {/* Toast: App-dəki tək qlobal ToastContainer istifadə olunur. */}

      {/* Başlıq */}
      <div className="flex items-center justify-between mb-6 max-w-5xl mx-auto">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-orange-500 to-amber-600 flex items-center justify-center shadow-sm">
            <Printer className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-xl font-bold ${text}`}>{t("printers.title")}</h1>
            <p className={`text-sm ${subText}`}>
              {t("printers.subtitle")}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            className={`p-2.5 rounded-xl border transition ${card} ${subText} hover:${text}`}
            title={t("common.refresh")}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Məzmun */}
      <div className="max-w-5xl mx-auto">
        {/* Printerlər Rezmo Admin panelində yaradılır və catalog stream-i ilə
            bura düşür. POS-da yaratmaq iki mənbə problemi yaradardı. */}
        <div
          className={`flex items-start gap-3 mb-4 p-3.5 rounded-xl border ${card}`}
        >
          <Lock className={`w-4 h-4 mt-0.5 shrink-0 ${subText}`} />
          <p className={`text-xs leading-relaxed ${subText}`}>
            {t("printers.managedInAdminBanner")}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-24">
            <PosLoader size="lg" label={t("common.loading")} />
          </div>
        ) : printers.length === 0 ? (
          <div className={`text-center py-20 rounded-2xl border ${card}`}>
            <Printer className={`w-12 h-12 mx-auto mb-3 ${subText}`} />
            <p className={`font-medium ${text}`}>{t("printers.emptyTitle")}</p>
            <p className={`text-sm mt-1 ${subText}`}>
              {t("printers.emptyText")}
            </p>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {printers.map((p) => {
              const conn = CONNECTIONS.find((c) => c.value === p.connection);
              const ConnIcon = conn?.icon || Wifi;
              const isBusy = busyId === p.id;
              // Cloud-dan gələn printer POS-da redaktə olunmur — növbəti
              // sync onsuz da dəyişikliyi üstündən yazardı.
              const isCloud = p.cloudRevision != null;
              return (
                <motion.div
                  key={p.id}
                  layout
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`rounded-2xl border p-4 ${card} ${
                    p.enabled ? "" : "opacity-60"
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                          p.enabled
                            ? "bg-orange-500/10 text-orange-500"
                            : "bg-gray-500/10 text-gray-400"
                        }`}
                      >
                        <ConnIcon className="w-5 h-5" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className={`font-semibold truncate ${text}`}>{p.name}</h3>
                          {p.enabled ? (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-green-500">
                              <CheckCircle2 className="w-3.5 h-3.5" /> {t("printers.active")}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400">
                              <PowerOff className="w-3.5 h-3.5" /> {t("printers.disabled")}
                            </span>
                          )}
                        </div>
                        <p className={`text-xs mt-0.5 ${subText}`}>
                          {stationLabel(p.station)} · {conn ? t(conn.labelKey) : p.connection}
                        </p>
                        <p className={`text-xs mt-0.5 font-mono ${subText}`}>
                          {p.address || "—"} · {p.paperWidth} {t("printers.chars")}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-dashed border-gray-500/20">
                    <button
                      onClick={() => handleTest(p)}
                      disabled={isBusy}
                      className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition disabled:opacity-50"
                    >
                      <Zap className="w-3.5 h-3.5" /> {t("printers.test")}
                    </button>
                    {isCloud ? (
                      <span
                        className={`inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-lg bg-gray-500/10 ${subText}`}
                        title={t("printers.managedInAdminHint")}
                      >
                        <Lock className="w-3.5 h-3.5" /> {t("printers.managedInAdmin")}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleDeleteLocal(p)}
                        disabled={isBusy}
                        className="ml-auto flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition disabled:opacity-50"
                        title={t("printers.deleteLocalHint")}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> {t("common.delete")}
                      </button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Son çap işləri ──────────────────────────────────────────────── */}
      <div className="max-w-5xl mx-auto mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className={`text-sm font-semibold ${text}`}>{t("printers.jobs.title")}</h2>
          <button
            onClick={loadJobs}
            disabled={jobsLoading}
            className={`p-2 rounded-lg border transition disabled:opacity-50 ${card} ${subText}`}
            title={t("common.refresh")}
          >
            <RefreshCw className={`w-4 h-4 ${jobsLoading ? "animate-spin" : ""}`} />
          </button>
        </div>

        {jobs.length === 0 ? (
          <div className={`rounded-2xl border p-6 text-center ${card}`}>
            <p className={`text-sm ${subText}`}>{t("printers.jobs.empty")}</p>
          </div>
        ) : (
          <div className={`rounded-2xl border divide-y divide-gray-500/10 overflow-hidden ${card}`}>
            {jobs.map((job) => {
              const printer = printers.find((p) => p.id === job.printerId);
              return (
                <button
                  key={job.id}
                  onClick={() => openPreview(job)}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-500/5 transition"
                >
                  <span
                    className={`shrink-0 w-2 h-2 rounded-full ${
                      job.status === "sent"
                        ? "bg-emerald-500"
                        : job.status === "pending" || job.status === "sending"
                          ? "bg-amber-500"
                          : "bg-red-500"
                    }`}
                    title={job.status}
                  />
                  <span className={`text-xs font-mono shrink-0 ${subText}`}>#{job.id}</span>
                  <span className={`text-sm font-medium shrink-0 ${text}`}>
                    {t(`printers.jobs.kind.${job.kind}`)}
                  </span>
                  <span className={`text-xs truncate ${subText}`}>
                    {printer?.name ?? job.printerId}
                  </span>
                  <span className={`ml-auto text-xs shrink-0 ${subText}`}>
                    {new Date(job.createdAt).toLocaleTimeString("az-AZ", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                  <Eye className={`w-4 h-4 shrink-0 ${subText}`} />
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Fiş önizləməsi — monospace, çap edilən mətnin eynisi. */}
      <AnimatePresence>
        {previewOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.96, y: 8 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.96, y: 8 }}
              onClick={(e) => e.stopPropagation()}
              className={`w-full max-w-md rounded-2xl border overflow-hidden ${card}`}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-500/10">
                <h3 className={`font-semibold text-sm ${text}`}>
                  {t("printers.jobs.previewTitle")}
                </h3>
                <button
                  onClick={() => setPreviewOpen(false)}
                  className={`p-1.5 rounded-lg hover:bg-gray-500/10 ${subText}`}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {preview === null ? (
                <div className="py-12 flex justify-center">
                  <PosLoader size="md" label={t("common.loading")} />
                </div>
              ) : (
                <>
                  {preview.lastError && (
                    <p className="mx-4 mt-3 px-3 py-2 rounded-lg bg-red-500/10 text-xs text-red-600 dark:text-red-400 break-words">
                      {preview.lastError}
                    </p>
                  )}
                  <pre
                    className={`m-4 p-3 rounded-lg text-[11px] leading-snug font-mono whitespace-pre overflow-x-auto ${
                      isDark ? "bg-black/40 text-gray-200" : "bg-gray-50 text-gray-800"
                    }`}
                  >
                    {preview.text}
                  </pre>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {confirmDialog}
    </div>
  );
};

export default PosPrinters;
